import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  Download,
  Building2,
  Warehouse,
  Layers,
  RefreshCw,
  Truck,
  Package,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  ChevronRight,
  User,
  Calendar,
  Loader2 } from
'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/shared/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle } from
"@/shared/components/ui/dialog";
import { cn } from '@/shared/utils';
import { toast } from 'sonner';

const API_URL = API_BASE;

// Status and type configs
const toStatuses = [
{ value: 'DRAFT', label: 'Draft' },
{ value: 'PENDING_APPROVAL', label: 'Pending Approval' },
{ value: 'APPROVED', label: 'Approved' },
{ value: 'PICKING', label: 'Picking' },
{ value: 'PICKED', label: 'Picked' },
{ value: 'IN_TRANSIT', label: 'In Transit' },
{ value: 'RECEIVED', label: 'Received' },
{ value: 'CLOSED', label: 'Closed' },
{ value: 'CANCELLED', label: 'Cancelled' }];


const toTypes = [
{ value: 'BRANCH_TRANSFER', label: 'Branch Transfer' },
{ value: 'WAREHOUSE_TRANSFER', label: 'Warehouse Transfer' },
{ value: 'ZONE_TRANSFER', label: 'Zone Transfer' },
{ value: 'REPLENISHMENT', label: 'Replenishment' }];


const statusConfig = {
  DRAFT: { color: 'bg-gray-100 text-gray-700 border-gray-200', icon: Package },
  PENDING_APPROVAL: { color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
  APPROVED: { color: 'bg-blue-50 text-blue-700 border-blue-200', icon: CheckCircle },
  PICKING: { color: 'bg-cyan-50 text-cyan-700 border-cyan-200', icon: Package },
  PICKED: { color: 'bg-violet-50 text-violet-700 border-violet-200', icon: CheckCircle },
  IN_TRANSIT: { color: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: Truck },
  RECEIVED: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  CLOSED: { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: CheckCircle },
  CANCELLED: { color: 'bg-red-50 text-red-700 border-red-200', icon: AlertCircle }
};

const typeIcons = {
  BRANCH_TRANSFER: Building2,
  WAREHOUSE_TRANSFER: Warehouse,
  ZONE_TRANSFER: Layers,
  REPLENISHMENT: RefreshCw
};

const priorityColors = {
  LOW: 'bg-gray-100 text-gray-600',
  MEDIUM: 'bg-blue-100 text-blue-700',
  HIGH: 'bg-orange-100 text-orange-700',
  URGENT: 'bg-red-100 text-red-700'
};

const TOCard = ({ to, onView }) => {const { t } = useTranslation("wms");
  const status = statusConfig[to.status] || statusConfig.DRAFT;
  const StatusIcon = status.icon;
  const statusLabel = toStatuses.find((s) => s.value === to.status)?.label || to.status;
  const TypeIcon = typeIcons[to.type] || Warehouse;
  const typeLabel = toTypes.find((t) => t.value === to.type)?.label || to.type;

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all cursor-pointer"
      onClick={() => onView(to)}
      data-testid={`to-card-${to.id}`}>
      
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <TypeIcon className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">{to.id}</span>
              <Badge variant="outline" className={cn("text-xs", priorityColors[to.priority])}>
                {to.priority}
              </Badge>
            </div>
            <div className="text-sm text-gray-600 mt-0.5">{typeLabel}</div>
          </div>
        </div>
        <Badge variant="outline" className={cn("flex items-center gap-1", status.color)}>
          <StatusIcon className="h-3 w-3" />
          {statusLabel}
        </Badge>
      </div>

      {/* From → To */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="text-xs text-gray-500 uppercase tracking-wide">{t("from", "From")}</div>
            <div className="font-medium text-gray-900 mt-1">{to.fromWarehouse}</div>
          </div>
          <div className="px-4">
            <ArrowRight className="h-5 w-5 text-gray-400" />
          </div>
          <div className="flex-1 text-right">
            <div className="text-xs text-gray-500 uppercase tracking-wide">{t("to", "To")}</div>
            <div className="font-medium text-gray-900 mt-1">{to.toWarehouse}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
        <div>
          <span className="text-gray-500">{t("items", "Items")}</span>
          <div className="font-medium text-gray-900">{to.totalItems}</div>
        </div>
        <div>
          <span className="text-gray-500">{t("quantity", "Quantity")}</span>
          <div className="font-medium text-gray-900">{to.totalQty.toLocaleString()}</div>
        </div>
        <div>
          <span className="text-gray-500">{t("value", "Value")}</span>
          <div className="font-medium text-gray-900">{t("r", "R$")}{to.totalValue.toLocaleString('pt-BR')}</div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span>{t("expected", "Expected:")}{new Date(to.expectedArrival).toLocaleDateString()}</span>
        </div>
        <ChevronRight className="h-5 w-5 text-gray-400" />
      </div>

      {to.status === 'IN_TRANSIT' && to.driverId &&
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-sm text-indigo-600">
          <Truck className="h-4 w-4" />
          <span>{t("vehicle", "Vehicle")}{to.vehicleId}{t("driver", "\u2022 Driver")}{to.driverId}</span>
        </div>
      }
    </div>);

};

export const TransferOrders = () => {const { t } = useTranslation("wms");
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedTO, setSelectedTO] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTransfers = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/transfers/`);
        if (response.ok) {
          const data = await response.json();
          // Transform API data to component format
          const transformedTransfers = data.map((t) => ({
            id: t.transfer_number || t.id,
            type: t.transfer_type?.toUpperCase()?.replace(/[\s-]/g, '_') || 'BRANCH_TRANSFER',
            fromWarehouse: t.source_warehouse || 'Source',
            fromWarehouseId: t.source_warehouse,
            toWarehouse: t.destination_warehouse || 'Destination',
            toWarehouseId: t.destination_warehouse,
            status: t.status?.toUpperCase()?.replace(/[\s-]/g, '_') || 'DRAFT',
            priority: t.priority?.toUpperCase() || 'MEDIUM',
            createdAt: t.created_at,
            createdBy: t.created_by || 'System',
            approvedAt: t.approved_at,
            approvedBy: t.approved_by,
            shippedAt: t.shipped_at,
            expectedArrival: t.due_date || t.expected_arrival,
            carrier: t.carrier || 'Internal Fleet',
            vehicleId: t.vehicle_id,
            driverId: t.driver_id,
            totalItems: t.items?.length || 0,
            totalQty: t.items?.reduce((sum, i) => sum + (i.quantity || 0), 0) || 0,
            totalValue: t.items?.reduce((sum, i) => sum + (i.quantity || 0) * (i.unit_cost || 10), 0) || 0,
            items: t.items?.map((item, idx) => ({
              id: idx + 1,
              sku: item.sku,
              name: item.product_name || item.sku,
              qty: item.quantity,
              lot: item.lot,
              expiry: item.expiry_date,
              fromLocation: item.source_location || 'TBD',
              toLocation: item.destination_location || 'TBD'
            })) || [],
            notes: t.reason || t.notes
          }));
          setTransfers(transformedTransfers);
        }
      } catch (err) {
        console.error('Failed to fetch transfers:', err);
        toast.error('Failed to load transfer orders');
      } finally {
        setLoading(false);
      }
    };

    fetchTransfers();
  }, []);

  const filteredTOs = transfers.filter((to) => {
    const matchesSearch = to.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    to.fromWarehouse.toLowerCase().includes(searchTerm.toLowerCase()) ||
    to.toWarehouse.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || to.status === statusFilter;
    const matchesType = typeFilter === 'all' || to.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const handleView = (to) => {
    setSelectedTO(to);
    setShowDetailDialog(true);
  };

  const stats = {
    total: transfers.length,
    inTransit: transfers.filter((t) => t.status === 'IN_TRANSIT').length,
    picking: transfers.filter((t) => t.status === 'PICKING').length,
    completed: transfers.filter((t) => t.status === 'RECEIVED').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>);

  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="transfer-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("transferOrders", "Transfer Orders")}</h1>
          <p className="text-sm text-gray-500 mt-1">{t("manageInterwarehouseAnd", "Manage inter-warehouse and branch transfers")}

          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />{t("export", "Export")}

          </Button>
          <Button className="gap-2 bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4" />{t("newTransfer", "New Transfer")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("totalTransfers", "Total Transfers")}</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("inTransit", "In Transit")}</div>
          <div className="text-2xl font-bold text-indigo-600 mt-1">{stats.inTransit}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("picking", "Picking")}</div>
          <div className="text-2xl font-bold text-cyan-600 mt-1">{stats.picking}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("completed", "Completed")}</div>
          <div className="text-2xl font-bold text-emerald-600 mt-1">{stats.completed}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search transfers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="to-search" />
            
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allStatuses", "All Statuses")}</SelectItem>
              {toStatuses.map((s) =>
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              )}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allTypes", "All Types")}</SelectItem>
              {toTypes.map((t) =>
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* TO Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredTOs.map((to) =>
        <TOCard key={to.id} to={to} onView={handleView} />
        )}
        {filteredTOs.length === 0 &&
        <div className="col-span-2 bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Warehouse className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-medium text-gray-900">{t("noTransferOrders", "No transfer orders found")}</h3>
            <p className="text-sm text-gray-500 mt-1">{t("tryAdjustingYour", "Try adjusting your filters")}</p>
          </div>
        }
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedTO?.id}
              {selectedTO &&
              <Badge variant="outline" className={cn(statusConfig[selectedTO.status]?.color)}>
                  {toStatuses.find((s) => s.value === selectedTO.status)?.label}
                </Badge>
              }
            </DialogTitle>
            <DialogDescription>
              {selectedTO && toTypes.find((t) => t.value === selectedTO.type)?.label}
            </DialogDescription>
          </DialogHeader>
          
          {selectedTO &&
          <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 uppercase">{t("from", "From")}</div>
                    <div className="font-medium text-gray-900 mt-1">{selectedTO.fromWarehouse}</div>
                  </div>
                  <ArrowRight className="h-6 w-6 text-gray-400 mx-4" />
                  <div className="flex-1 text-right">
                    <div className="text-xs text-gray-500 uppercase">{t("to", "To")}</div>
                    <div className="font-medium text-gray-900 mt-1">{selectedTO.toWarehouse}</div>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-medium">{t("item", "Item")}</th>
                      <th className="text-center p-3 font-medium">{t("qty", "Qty")}</th>
                      <th className="text-left p-3 font-medium">{t("fromLocation", "From Location")}</th>
                      <th className="text-left p-3 font-medium">{t("toLocation", "To Location")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTO.items.map((item) =>
                  <tr key={item.id} className="border-t">
                        <td className="p-3">
                          <div className="font-medium">{item.name}</div>
                          <div className="text-gray-500 text-xs">{item.sku}</div>
                          {item.lot && <div className="text-xs text-gray-400">{t("lot", "Lot:")}{item.lot}</div>}
                        </td>
                        <td className="p-3 text-center">{item.qty}</td>
                        <td className="p-3 font-mono text-sm">{item.fromLocation}</td>
                        <td className="p-3 font-mono text-sm">{item.toLocation || 'TBD'}</td>
                      </tr>
                  )}
                  </tbody>
                </table>
              </div>

              {selectedTO.notes &&
            <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-sm text-gray-500">{t("notes", "Notes:")}</span>
                  <p className="text-sm mt-1">{selectedTO.notes}</p>
                </div>
            }
            </div>
          }

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>{t("close", "Close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};